import React from 'react';
import { motion } from 'framer-motion';
import { Gem, Truck, RotateCcw, ArrowRight } from 'lucide-react';
import FeatureCard from './FeatureCard';

// Animation variants
const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const fadeInUp = {
  initial: {
    opacity: 0,
    y: 60,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const arrowAnimation = {
  initial: {
    opacity: 0,
    x: -20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      delay: 1,
      ease: "easeOut",
    },
  },
};

const HowItWorksSection = () => {
  const features = [
    {
      icon: Gem,
      title: "Choose a Subscription",
      description: "Select the plan that fits your child's age and interests."
    },
    {
      icon: Truck,
      title: "Receive Safe & Sanitized Toys",
      description: "We deliver premium toys right to your doorstep."
    },
    {
      icon: RotateCcw,
      title: "Swap Anytime",
      description: "Return old toys and get new ones whenever you want."
    }
  ];

  return (
    <motion.section
      className="w-full flex flex-col items-center justify-center px-6 py-12 text-center relative"
      aria-labelledby="workflow-heading"
      role="region"
      variants={staggerContainer}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.3 }}
    >
      {/* Section Heading */}
      <motion.div
        variants={fadeInUp}
        className="mb-12 max-w-4xl mx-auto"
      >
        <h2
          id="workflow-heading"
          className="text-4xl font-bold text-sky-950 mb-4 max-sm:text-3xl"
        >
          How it works
        </h2>
        <p className="text-lg leading-7 text-gray-600 max-w-[500px] mx-auto max-sm:text-base max-sm:px-4">
          A simple workflow to help you find tasks, quote faster, and get paid securely.
        </p>
      </motion.div>

      {/* Features Grid with Arrows */}
      <div className="relative max-w-[1400px] mx-auto w-full">
        <motion.div
          className="grid grid-cols-3 gap-2 justify-center items-center
                 
                     sm:grid-cols-1 sm:gap-8 sm:max-w-sm sm:mx-auto
                     lg:grid-cols-3 lg:gap-6 lg:max-w-[1400px]"
          variants={staggerContainer}
          role="list"
          aria-label="How it works steps"
        >
          {features.map((feature, index) => (
            <div key={index} className="relative flex justify-center items-center w-full" role="listitem">
              <motion.div
                variants={fadeInUp}
                className="flex justify-center h-full w-full"
              >
                <FeatureCard {...feature} />
              </motion.div>

              {/* Arrows - Only show on desktop */}
              {index < features.length - 1 && (
                <>
                  {/* Desktop Horizontal Arrows (lg and above) */}
                  <motion.div
                    variants={arrowAnimation}
                    className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2 z-10"
                    aria-hidden="true"
                  >
                    <div className="flex items-center">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <div className="w-2 h-2 bg-green-300 rounded-full"></div>
                      </div>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="ml-2"
                      >
                        <ArrowRight className="w-6 h-6 text-green-500" aria-hidden="true" />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Tablet Vertical Arrows - HIDDEN */}
                  <motion.div
                    variants={arrowAnimation}
                    className="hidden"
                    aria-hidden="true"
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex flex-col space-y-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full mx-auto"></div>
                        <div className="w-2 h-2 bg-green-400 rounded-full mx-auto"></div>
                        <div className="w-2 h-2 bg-green-300 rounded-full mx-auto"></div>
                      </div>
                      <motion.div
                        animate={{ y: [0, 5, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="mt-2"
                      >
                        <ArrowRight className="w-6 h-6 text-green-500 transform rotate-90" aria-hidden="true" />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Mobile Arrows - Hidden */}
                  <motion.div
                    variants={arrowAnimation}
                    className="hidden"
                    aria-hidden="true"
                  >
                    <div className="flex items-center">
                      <div className="flex space-x-0.5">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <div className="w-1.5 h-1.5 bg-green-300 rounded-full"></div>
                      </div>
                      <motion.div
                        animate={{ x: [0, 3, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="ml-1"
                      >
                        <ArrowRight className="w-4 h-4 text-green-500" aria-hidden="true" />
                      </motion.div>
                    </div>
                  </motion.div>
                </>
              )}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Decorative floating elements */}
      <motion.div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ delay: 2, duration: 1 }}
        aria-hidden="true"
      >
        {/* Floating dots */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/6 w-1 h-1 bg-pink-400 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
      </motion.div>
    </motion.section>
  );
};

export default HowItWorksSection;
